import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { ShieldCheck, Zap, BadgeCheck, ArrowRight, Star, Sparkles } from "lucide-react";
import { NEXUS_LOGO } from "../lib/api";
import { useResults } from "./ResultsWall";
import RecentSignups from "./RecentSignups";

/**
 * Clean, static-scrolling hero — no pinned/sticky sections, no scroll-scrub.
 * Two columns on desktop (copy left, phone card right), stacks on mobile.
 * Cosmic vibe comes from the Starfield background component.
 */

const TRUST = [
  { icon: ShieldCheck, label: "Escrow protected" },
  { icon: BadgeCheck, label: "Verified creators" },
  { icon: Zap, label: "Instant payouts" },
];

function HeroRatingRow() {
  const data = useResults(1);
  const t = data?.totals;
  if (!t?.count || !t.avg_rating) return null;
  const full = Math.round(t.avg_rating);
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-2 text-[11px] text-zinc-400" data-testid="hero-rating-row">
      <div className="flex gap-0.5">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} className={`h-3 w-3 ${i < full ? "fill-amber-300 text-amber-300" : "text-amber-300/30"}`} />
        ))}
      </div>
      <span className="tabular-nums">{t.avg_rating.toFixed(1)}</span>
      <span className="text-zinc-600">·</span>
      <span>average across {t.count.toLocaleString("en-GB")} completed deals with {t.brands} brands</span>
      <span className="text-zinc-600">·</span>
      <Link to="/results" className="underline hover:text-white" data-testid="hero-rating-link">see results</Link>
    </div>
  );
}

export default function CinematicHero() {
  return (
    <section className="relative pt-28 pb-20 md:pt-32 md:pb-28" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          {/* LEFT — copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="space-y-6"
          >
            {/* Nexus logo — big and prominent like Aug 14 */}
            <img
              src={NEXUS_LOGO}
              alt="Nexus"
              className="h-32 sm:h-40 md:h-48 lg:h-56 w-auto object-contain drop-shadow-[0_10px_60px_rgba(157,78,221,0.4)]"
              data-testid="hero-logo"
            />

            <div className="inline-flex items-center gap-2 h-7 px-3 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-sm text-[10px] uppercase tracking-[0.28em] text-zinc-300 font-semibold">
              <ShieldCheck className="h-3 w-3 text-emerald-300" />
              Escrow-protected creator marketplace
            </div>

            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] text-white">
              The trust layer for the{" "}
              <span className="bg-gradient-to-r from-[#FF107A] via-[#9D4EDD] to-[#00D4FF] bg-clip-text text-transparent">
                creator economy
              </span>
              .
            </h1>

            <p className="text-base sm:text-lg text-zinc-200 leading-relaxed max-w-xl" data-testid="hero-what-is-nexus">
              <span className="text-white font-semibold">Nexus is the marketplace where brands hire vetted TikTok, Instagram and YouTube creators for paid content.</span>{" "}
              The brand funds the deal up front through Stripe, the creator delivers in-app, and payment is released the moment it's approved — creators keep 100%. UK-based, creators worldwide.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/auth?mode=signup&role=brand" data-testid="hero-brand-cta">
                <Button className="rounded-full h-12 px-6 nexus-gradient text-white border-0 font-semibold text-sm shadow-[0_10px_40px_-10px_rgba(255,16,122,0.6)] hover:opacity-95 transition-opacity">
                  I'm a Brand <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to="/auth?mode=signup&role=creator" data-testid="hero-creator-cta">
                <Button
                  variant="outline"
                  className="rounded-full h-12 px-6 bg-white/[0.04] border-white/15 text-white hover:bg-white/[0.08] hover:text-white font-semibold text-sm backdrop-blur-sm"
                >
                  I'm a Creator
                </Button>
              </Link>
            </div>

            <Link to="/how-it-works" className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-white underline underline-offset-4 decoration-white/20" data-testid="hero-how-it-works-link">
              See how Nexus works <ArrowRight className="h-3.5 w-3.5" />
            </Link>

            {/* Trust chips */}
            <div className="flex flex-wrap gap-2 pt-4">
              {TRUST.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-sm text-[11px] text-zinc-300"
                >
                  <Icon className="h-3.5 w-3.5 text-nexus-pink" />
                  <span className="uppercase tracking-widest text-[10px] font-semibold">
                    {label}
                  </span>
                </div>
              ))}
            </div>

            <HeroRatingRow />
            <div className="pt-3"><RecentSignups /></div>
          </motion.div>

          {/* RIGHT — phone / preview card, inline & static (no sticky, no 3D tilt) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
            className="relative flex justify-center lg:justify-end"
          >
            <HeroPhoneMock />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* --------- Inline phone mock (static, decorative) --------- */

function HeroPhoneMock() {
  return (
    <div className="relative" data-testid="landing-phone-mock">
      {/* Ambient halo */}
      <div
        className="absolute -inset-10 rounded-[80px] blur-3xl opacity-70 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 50%, rgba(157,78,221,0.35), rgba(255,16,122,0.15) 45%, transparent 70%)",
        }}
      />

      {/* Phone body */}
      <div className="relative w-[260px] sm:w-[300px] rounded-[46px] border border-white/10 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black shadow-[0_50px_100px_-20px_rgba(255,16,122,0.35),0_0_40px_rgba(157,78,221,0.2)] overflow-hidden">
        {/* Notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 rounded-full bg-black z-30 border border-white/10" />

        {/* Screen */}
        <div className="relative m-[7px] aspect-[9/19] rounded-[38px] overflow-hidden bg-gradient-to-b from-zinc-950 via-black to-zinc-950 p-4 pt-9 flex flex-col gap-3">
          {/* Header pill */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <img src={NEXUS_LOGO} alt="" className="h-4 w-4 rounded" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-white">
                Nexus
              </span>
            </div>
            <span className="text-[8px] uppercase tracking-widest text-emerald-300 flex items-center gap-1">
              <ShieldCheck className="h-2.5 w-2.5" /> Live
            </span>
          </div>

          {/* Deal card */}
          <div className="rounded-2xl p-3 border border-white/10 bg-white/[0.02] backdrop-blur-sm">
            <div className="text-[9px] uppercase tracking-widest text-nexus-blue mb-1">
              Campaign accepted
            </div>
            <div className="text-[13px] font-semibold text-white leading-tight mb-2">
              Streetwear brand × 3 street-style Reels
            </div>
            <div className="flex items-center justify-between text-[10px] text-zinc-400">
              <span>Escrow funded</span>
              <span className="font-bold text-white tabular-nums">£800</span>
            </div>
            <div className="relative h-1 rounded-full bg-white/5 mt-1.5 overflow-hidden">
              <motion.div
                className="absolute inset-y-0 left-0 nexus-gradient"
                animate={{ width: ["0%", "62%"] }}
                transition={{ duration: 2, ease: "easeOut" }}
              />
            </div>
          </div>

          {/* Next step */}
          <div className="rounded-2xl p-3 border border-white/10 bg-white/[0.02]">
            <div className="text-[9px] uppercase tracking-widest text-nexus-pink mb-1">
              Next step
            </div>
            <div className="text-[11px] text-white leading-snug">
              Film & upload the first draft — brand reviews within 48h.
            </div>
          </div>

          {/* XP row */}
          <div className="rounded-2xl p-2.5 border border-white/10 bg-white/[0.02]">
            <div className="flex items-center justify-between text-[10px] mb-1">
              <span className="text-zinc-400 flex items-center gap-1">
                <Sparkles className="h-2.5 w-2.5 text-nexus-purple" /> Silver
              </span>
              <span className="text-white font-bold tabular-nums">+120 XP</span>
            </div>
            <div className="h-1 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full nexus-gradient" style={{ width: "68%" }} />
            </div>
          </div>

          {/* Bottom action */}
          <div className="mt-auto h-9 rounded-full nexus-gradient flex items-center justify-center text-[11px] font-bold text-white">
            Open Deal Room
          </div>
        </div>

        {/* Reflective sheen */}
        <div
          className="pointer-events-none absolute inset-0 rounded-[46px]"
          style={{
            background:
              "linear-gradient(120deg, rgba(255,255,255,0.12) 0%, transparent 30%, transparent 70%, rgba(255,255,255,0.05) 100%)",
          }}
        />
      </div>

      {/* Floating trust chips (STATIC placement, subtle animation only) */}
      <motion.div
        className="hidden sm:flex absolute -top-4 -right-4 h-12 px-3 rounded-2xl border border-emerald-500/25 bg-black/70 backdrop-blur-md items-center gap-2 shadow-2xl"
        animate={{ y: [-3, 3, -3] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      >
        <ShieldCheck className="h-4 w-4 text-emerald-300" />
        <span className="text-[10px] uppercase tracking-widest text-white font-bold">
          £320 in escrow
        </span>
      </motion.div>
      <motion.div
        className="hidden sm:flex absolute -bottom-4 -left-6 h-11 px-3 rounded-2xl border border-nexus-pink/30 bg-black/70 backdrop-blur-md items-center gap-2 shadow-2xl"
        animate={{ y: [3, -3, 3] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      >
        <Zap className="h-3.5 w-3.5 text-nexus-pink" />
        <span className="text-[10px] uppercase tracking-widest text-white font-bold">
          Paid in 24h
        </span>
      </motion.div>
    </div>
  );
}
